import { useEffect, useState } from "react";
import { Link, useSearchParams, type To } from "react-router";
import { paths } from "@shared-core/routing/routes";
import {
  formatCount,
  formatCurrencyGbp,
  formatDateOrDash,
} from "@shared-core/domain/formatters";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { useListQuery } from "@shared-core/domain/useListQuery";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import type { CustomerQuoteListOptions } from "@shared-core/data/customer-adapter";
import type {
  CustomerQuoteStatus,
  CustomerQuoteSummary,
} from "@shared-core/data/customer-api-types";
import { usePortalViewer } from "./PortalViewerContext";
import { Card } from "../../app/components/primitives/card";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { cn } from "../../app/components/primitives/utils";
import { AlertTriangle, FileText, Search } from "lucide-react";

// The customer's CIS quotes (customer-storefront P4f, docs 26 section 9B and 28 section 5A): this page lists
// them and says where each stands; a row opens the quote's own page (PortalQuoteDetailPage), where it is looked over,
// accepted or declined (the emailed link opens the same page body). The list names no `_self` for a quote, so
// the row's address is built from the number.
//
// Filter, search and pagination run server-side (docs/list-endpoints.md, GET /views/customer/quotes): this page
// is a thin binding over CustomerDataAdapter.listQuotes, never a client-side filter, sort or slice.

type Filter = "all" | "open" | "accepted" | "closed";

const FILTERS: {
  id: Filter;
  label: string;
  statuses: CustomerQuoteStatus[] | undefined;
}[] = [
  { id: "all", label: "All", statuses: undefined },
  { id: "open", label: "Open", statuses: ["published"] },
  { id: "accepted", label: "Accepted", statuses: ["accepted"] },
  {
    id: "closed",
    label: "Closed",
    statuses: ["declined", "expired", "withdrawn"],
  },
];

const PAGE_SIZE = 25;

function StatusBadge({ quote }: { quote: CustomerQuoteSummary }) {
  const variant =
    quote.status === "published"
      ? "warning"
      : quote.status === "accepted"
        ? "success"
        : "outline";
  return <Badge variant={variant}>{quote.status_label}</Badge>;
}

/** Where the quote stands in one line: how long it is open for, or when and how it closed. */
function standing(quote: CustomerQuoteSummary): string {
  if (quote.status === "published")
    return `Open until ${formatDateOrDash(quote.expires_at)}`;
  const closed = quote.decided_at ?? quote.expires_at;
  if (quote.status === "accepted" && quote.order_no)
    return `Accepted ${formatDateOrDash(closed)} · Order ${quote.order_no}`;
  return `${quote.status_label} ${formatDateOrDash(closed)}`;
}

function QuoteCard({
  quote,
  showContact,
  to,
}: {
  quote: CustomerQuoteSummary;
  showContact: boolean;
  to: To;
}) {
  return (
    <Card className="p-4 transition-colors hover:bg-accent/40">
      <Link to={to} className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{quote.title}</span>
            <StatusBadge quote={quote} />
          </div>
          <p className="text-sm text-muted-foreground">
            {[
              quote.quote_no,
              `Quote date ${formatDateOrDash(quote.published_at)}`,
              showContact ? `To ${quote.contact_name}` : undefined,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <p className="text-sm">{standing(quote)}</p>
        </div>
        <p className="shrink-0 text-right tabular-nums">
          {formatCurrencyGbp(quote.total_pence / 100)}
          <span className="block text-xs text-muted-foreground">ex VAT</span>
        </p>
      </Link>
    </Card>
  );
}

export function PortalQuotesPage() {
  const adapter = useCustomerDataAdapter();
  const { viewKey, sessionState, buildPortalUrl } = usePortalViewer();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterParam = searchParams.get("show");
  const filter: Filter = FILTERS.find((f) => f.id === filterParam)?.id ?? "all";

  const [queryInput, setQueryInput] = useState("");
  const debouncedQuery = useDebouncedValue(queryInput, 300);
  const [page, setPage] = useState(1);

  // Changing the filter or the search starts again at the first page.
  useEffect(() => {
    setPage(1);
  }, [filter, debouncedQuery]);

  const { state, retry } = useListQuery(() => {
    const options: CustomerQuoteListOptions = {
      page,
      pageSize: PAGE_SIZE,
    };
    const statuses = FILTERS.find((f) => f.id === filter)?.statuses;
    if (statuses) options.status = statuses;
    if (debouncedQuery.trim()) options.q = debouncedQuery.trim();
    return adapter.listQuotes(options);
  }, [adapter, viewKey, filter, page, debouncedQuery]);

  // Someone who sees the whole customer's quotes is told whom each was sent to; for own work it is always them.
  const showContact =
    sessionState.status === "success" &&
    sessionState.data.visibility === "customer_wide";

  function selectFilter(next: Filter) {
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete("show");
    else params.set("show", next);
    setSearchParams(params, { replace: true });
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Quotes</h1>
        <p className="text-sm text-muted-foreground">
          Quotes we have sent you. Open one to look it over, accept it or
          decline it.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-md border p-0.5">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              aria-pressed={filter === f.id}
              onClick={() => selectFilter(f.id)}
              className={cn(
                "rounded px-3 py-1.5 text-sm font-medium transition-colors",
                filter === f.id
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            placeholder="Search quote number, title or item"
            className="pl-8"
            aria-label="Search quotes"
          />
        </div>
      </div>

      {state.status === "loading" && (
        <Card className="flex flex-col items-center gap-2 p-10 text-center">
          <p className="text-sm text-muted-foreground">Loading your quotes…</p>
        </Card>
      )}

      {state.status === "error" && (
        <Card className="flex flex-col items-center gap-3 p-10 text-center">
          <AlertTriangle className="size-6 text-muted-foreground" />
          <p className="font-medium">We couldn&apos;t load your quotes</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            {state.message}
          </p>
          <Button variant="outline" onClick={retry}>
            Try again
          </Button>
        </Card>
      )}

      {state.status === "success" && state.data.items.length === 0 && (
        <Card className="flex flex-col items-center gap-2 p-10 text-center">
          <FileText className="size-6 text-muted-foreground" />
          <p className="font-medium">
            {debouncedQuery
              ? "Nothing matches that search"
              : filter === "all"
                ? "No quotes yet"
                : "No quotes in this view"}
          </p>
          <p className="max-w-sm text-sm text-muted-foreground">
            {debouncedQuery
              ? "Try a shorter search, or just the quote number."
              : "Quotes we send you will appear here."}
          </p>
        </Card>
      )}

      {state.status === "success" && state.data.items.length > 0 && (
        <>
          <Card className="hidden overflow-x-auto p-0 md:block">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">Quote date</TableHead>
                  <TableHead>Quote</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                  <TableHead className="w-64">Standing</TableHead>
                  <TableHead className="w-28 text-right">
                    Total ex VAT
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.data.items.map((quote) => (
                  <TableRow key={quote.quote_no}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {formatDateOrDash(quote.published_at)}
                    </TableCell>
                    <TableCell className="min-w-0 max-w-0 whitespace-normal">
                      <Link
                        to={buildPortalUrl(
                          paths.portalQuoteDetail({ quoteNo: quote.quote_no }),
                        )}
                        className="line-clamp-2 break-words font-medium hover:underline"
                      >
                        {quote.title}
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        {[
                          quote.quote_no,
                          showContact ? `To ${quote.contact_name}` : undefined,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </TableCell>
                    <TableCell>
                      <StatusBadge quote={quote} />
                    </TableCell>
                    <TableCell className="whitespace-normal text-sm">
                      {standing(quote)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrencyGbp(quote.total_pence / 100)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div className="space-y-3 md:hidden">
            {state.data.items.map((quote) => (
              <QuoteCard
                key={quote.quote_no}
                quote={quote}
                showContact={showContact}
                to={buildPortalUrl(
                  paths.portalQuoteDetail({ quoteNo: quote.quote_no }),
                )}
              />
            ))}
          </div>

          {state.data.totalPages > 1 && (
            <div className="flex items-center justify-between gap-3 pt-1">
              <p className="text-xs text-muted-foreground">
                Page {state.data.page} of {state.data.totalPages} ·{" "}
                {formatCount(state.data.total)} quote
                {state.data.total !== 1 ? "s" : ""}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= state.data.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
